import { Check } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface StepDefinition {
  id: string;
  label: string;
}

interface StepperProps {
  steps: readonly StepDefinition[];
  current: number;
  className?: string;
}

export function Stepper({ steps, current, className }: StepperProps) {
  return (
    <nav aria-label="Progress" className={className}>
      <p className="sr-only">
        Step {current + 1} of {steps.length}: {steps[current]?.label}
      </p>
      <ol className="flex items-center">
        {steps.map((step, index) => {
          const done = index < current;
          const active = index === current;
          return (
            <li key={step.id} className={cn('flex items-center', index < steps.length - 1 && 'flex-1')}>
              <div className="flex items-center gap-2" aria-current={active ? 'step' : undefined} title={step.label}>
                <span
                  className={cn(
                    'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-semibold transition-colors',
                    done && 'border-primary bg-primary text-primary-fg',
                    active && 'border-primary bg-primary-soft text-primary',
                    !done && !active && 'border-line-strong bg-surface text-ink-muted',
                  )}
                >
                  {done ? <Check size={14} aria-hidden="true" /> : index + 1}
                </span>
                <span
                  className={cn(
                    'whitespace-nowrap text-[13px]',
                    active ? 'font-semibold text-ink' : 'sr-only',
                  )}
                >
                  {step.label}
                </span>
              </div>
              {index < steps.length - 1 && (
                <span aria-hidden="true" className={cn('mx-3 h-px min-w-4 flex-1', done ? 'bg-primary' : 'bg-line-strong')} />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
